/**
 * ShopifyTaggingSection — write production status back to Shopify as order
 * tags (extracted from the old god component's `renderShopifyTagging`).
 *
 * The old page autosaved this toggle on change; the Console dirty model
 * requires an explicit Save, so the toggle now stages a pending value and
 * registers a 'shopify-tagging' dirty entry like every other section.
 */
import { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';
import { SettingRow } from '../components/console/SettingRow';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

export function ShopifyTaggingSection() {
  const shopifyTaggingQuery = trpc.productionSettings.getShopifyTaggingSettings.useQuery();
  const updateShopifyTaggingMutation =
    trpc.productionSettings.updateShopifyTaggingSettings.useMutation();

  const tagPrefix = shopifyTaggingQuery.data?.shopifyOrderTagPrefix ?? 'printrun';

  const [shopifyTaggingEnabled, setShopifyTaggingEnabled] = useState(false);
  const [shopifyTaggingLoaded, setShopifyTaggingLoaded] = useState(false);
  const [pending, setPending] = useState<boolean | null>(null);

  useEffect(() => {
    if (shopifyTaggingQuery.data && !shopifyTaggingLoaded) {
      setShopifyTaggingEnabled(shopifyTaggingQuery.data.shopifyOrderTaggingEnabled);
      setShopifyTaggingLoaded(true);
    }
  }, [shopifyTaggingQuery.data, shopifyTaggingLoaded]);

  const isDirty = pending !== null && pending !== shopifyTaggingEnabled;
  const value = pending ?? shopifyTaggingEnabled;

  useDirtyRegistration(
    'shopify-tagging',
    isDirty
      ? {
          label: 'Shopify order tagging',
          save: async () => {
            const next = pending;
            try {
              await updateShopifyTaggingMutation.mutateAsync({ shopifyOrderTaggingEnabled: next });
            } catch (error) {
              toast.error(
                `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
              );
              throw error;
            }
            setShopifyTaggingEnabled(next);
            setPending(null);
            toast.success(next ? 'Order tagging enabled' : 'Order tagging disabled');
            shopifyTaggingQuery.refetch();
          },
          discard: () => setPending(null),
        }
      : null,
  );

  return (
    <SettingsSection
      id="shopify-tagging"
      title="Shopify order tagging"
      description="Write production status back to Shopify as order tags."
      icon={Tag}
      scope="org"
    >
      {shopifyTaggingQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-muted-foreground">Loading…</span>
        </div>
      ) : (
        <div className="space-y-4">
          <SettingRow
            htmlFor="shopify-tagging-enabled"
            label="Enable Order Tagging"
            description={
              value ? (
                <>
                  Tagging is <span className="font-medium text-green-600">enabled</span>. Tags
                  will be synced to Shopify.
                </>
              ) : (
                <>
                  Tagging is <span className="font-medium text-amber-600">disabled</span>. No tags
                  will be written to Shopify.
                </>
              )
            }
          >
            <Switch
              id="shopify-tagging-enabled"
              checked={value}
              onCheckedChange={(checked) => setPending(checked)}
              disabled={updateShopifyTaggingMutation.isPending}
            />
          </SettingRow>

          <Separator />

          {/* Tags Reference */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">
              Tags managed by this feature:
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                <code className="mt-0.5 shrink-0 rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                  {tagPrefix}:in-production
                </code>
                <p className="text-sm text-muted-foreground">
                  Added when orders are included in a production batch. Indicates the order has
                  entered the production workflow.
                </p>
              </div>
              <div className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3">
                <code className="mt-0.5 shrink-0 rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                  {tagPrefix}:shipped
                </code>
                <p className="text-sm text-muted-foreground">
                  Replaces <code className="rounded bg-muted px-1 text-xs">{tagPrefix}:in-production</code>{' '}
                  when an order is confirmed shipped or manually completed.
                </p>
              </div>
            </div>
          </div>

          {/* Prerequisite Note */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Prerequisite:</span> Your Shopify app must have the{' '}
              <code className="rounded bg-blue-100 px-1 text-xs font-semibold">write_orders</code>{' '}
              scope. If the scope is missing, tagging operations will fail silently without
              affecting production workflows.
            </p>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
